import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Search, Trash2, Download, RefreshCw, Users, CalendarDays, Store, ArrowUpDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "gomill_admin_key";

const TABS = [
  { id: "subscribers", label: "Subscribers", icon: Users, endpoint: "subscribers", columns: ["created_at", "email"] },
  { id: "events", label: "Event Enquiries", icon: CalendarDays, endpoint: "event-enquiries", columns: ["created_at", "name", "phone", "email", "event_type", "event_date", "event_location", "expected_guests", "preferred_time", "message"] },
  { id: "franchise", label: "Franchise Enquiries", icon: Store, endpoint: "franchise-enquiries", columns: ["created_at", "full_name", "email", "phone", "preferred_format", "city"] },
];

function LoginCard({ onSuccess }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Key": key.trim() } });
      localStorage.setItem(STORAGE_KEY, key.trim());
      toast.success("Welcome, admin");
      onSuccess(key.trim());
    } catch {
      toast.error("Invalid admin key");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md bg-[#161616] border border-white/5 rounded-2xl p-8 shadow-2xl">
        <div className="w-12 h-12 rounded-xl bg-[#E5B94A] inline-flex items-center justify-center mb-6">
          <Lock className="w-6 h-6 text-[#0F3824]" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">GoMill Admin</h1>
        <p className="text-white/60 text-sm mt-1 mb-6">Enter your admin key to access the panel.</p>
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Admin key"
          className="w-full bg-white text-black rounded-xl px-4 py-3.5 outline-none focus:ring-2 focus:ring-[#E5B94A] placeholder-black/40"
          data-testid="admin-key-input"
          autoFocus
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full mt-4 bg-[#E5B94A] hover:bg-[#D9AC38] disabled:opacity-60 text-black font-bold rounded-xl py-3.5 transition-colors"
          data-testid="admin-login-btn"
        >
          {busy ? "Verifying…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function toCsv(rows, columns) {
  if (!rows.length) return "";
  const header = columns.join(",");
  const body = rows.map((r) =>
    columns
      .map((c) => {
        const v = r[c] ?? "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      })
      .join(",")
  );
  return [header, ...body].join("\n");
}

function download(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function Dashboard({ adminKey, onLogout }) {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [dataByTab, setDataByTab] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const tab = useMemo(() => TABS.find((t) => t.id === activeTab), [activeTab]);

  const fetchTab = async (tabDef) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${tabDef.endpoint}`, { headers: { "X-Admin-Key": adminKey } });
      setDataByTab((prev) => ({ ...prev, [tabDef.id]: res.data.items || [] }));
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dataByTab[activeTab]) fetchTab(tab);
    setQ("");
    setSortKey("created_at");
    setSortDir("desc");
    // eslint-disable-next-line
  }, [activeTab]);

  const currentRows = dataByTab[activeTab] || [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = needle
      ? currentRows.filter((r) =>
          tab.columns.some((c) => String(r[c] ?? "").toLowerCase().includes(needle))
        )
      : currentRows;
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [currentRows, q, sortKey, sortDir, tab.columns]);

  const toggleSort = (col) => {
    if (sortKey === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(col);
      setSortDir("asc");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record? It will be archived (soft delete).")) return;
    try {
      await axios.delete(`${API}/admin/${tab.endpoint}/${id}`, { headers: { "X-Admin-Key": adminKey } });
      setDataByTab((prev) => ({ ...prev, [activeTab]: (prev[activeTab] || []).filter((r) => r.id !== id) }));
      toast.success("Record deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleExport = () => {
    if (!filtered.length) {
      toast.info("Nothing to export");
      return;
    }
    const csv = toCsv(filtered, tab.columns);
    download(`gomill-${tab.endpoint}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success("CSV downloaded");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="sticky top-0 z-10 backdrop-blur bg-black/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="w-9 h-9 rounded-lg bg-[#E5B94A] inline-flex items-center justify-center">
            <Lock className="w-4 h-4 text-black" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-lg leading-none">GoMill Admin</div>
            <div className="text-white/50 text-xs mt-0.5">Enquiries & subscribers dashboard</div>
          </div>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm border border-white/10 hover:border-white/20 rounded-lg px-3 py-2 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = dataByTab[t.id]?.length;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 border transition-colors ${
                  active
                    ? "bg-[#E5B94A] text-black border-[#E5B94A] font-semibold"
                    : "bg-white/5 text-white/80 border-white/10 hover:bg-white/10"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.label}</span>
                {typeof count === "number" && (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-md ${active ? "bg-black/20" : "bg-white/10"}`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px] max-w-lg">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="w-full bg-white/5 border border-white/10 focus:border-[#E5B94A] rounded-lg pl-9 pr-3 py-2.5 outline-none text-sm"
              data-testid="admin-search"
            />
          </div>
          <button
            onClick={() => fetchTab(tab)}
            className="inline-flex items-center gap-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2.5"
            data-testid="admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 text-sm bg-[#E5B94A] hover:bg-[#D9AC38] text-black font-semibold rounded-lg px-3 py-2.5"
            data-testid="admin-export"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.04] text-white/60 uppercase text-[11px] tracking-wider">
                <tr>
                  {tab.columns.map((c) => (
                    <th
                      key={c}
                      onClick={() => toggleSort(c)}
                      className="text-left px-4 py-3 font-medium cursor-pointer hover:text-white select-none whitespace-nowrap"
                      data-testid={`col-${c}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.replace(/_/g, " ")}
                        <ArrowUpDown className={`w-3 h-3 ${sortKey === c ? "text-[#E5B94A]" : "text-white/30"}`} />
                      </span>
                    </th>
                  ))}
                  <th className="text-right px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tab.columns.length + 1} className="text-center py-16 text-white/40">Loading…</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tab.columns.length + 1} className="text-center py-16 text-white/40" data-testid="empty-state">
                      No records{q ? " match your search" : ""}.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`row-${row.id}`}>
                      {tab.columns.map((c) => (
                        <td key={c} className="px-4 py-3 text-white/85 align-top max-w-[320px]">
                          {c === "created_at" ? (
                            <span className="text-white/60 whitespace-nowrap">{formatDate(row[c])}</span>
                          ) : (
                            <span className="break-words">{row[c] || "—"}</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(row.id)}
                          className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-md px-2.5 py-1.5 text-xs"
                          data-testid={`delete-${row.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3 text-xs text-white/40" data-testid="row-count">
          Showing {filtered.length} of {currentRows.length} records
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!adminKey) {
        setChecking(false);
        return;
      }
      try {
        await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Key": adminKey } });
        if (!cancelled) setVerified(true);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setAdminKey("");
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAdminKey("");
    setVerified(false);
    toast("Logged out");
  };

  if (checking) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">Loading…</div>;
  }

  if (!verified) {
    return (
      <LoginCard
        onSuccess={(k) => {
          setAdminKey(k);
          setVerified(true);
        }}
      />
    );
  }

  return <Dashboard adminKey={adminKey} onLogout={handleLogout} />;
}
